import { useState, useEffect } from 'react'

import { Button, Grid, Select, Span, TableFooter } from 'components'
import { DeleteIcon } from 'components/Icons'
import { useModal, useLoading } from 'hooks'
import {
  managementUnitService,
  ParamCockpitServices
} from 'services/NGT/Parameterization'
import { SPACING, TYPOGRAPHY } from 'utils'

import {
  TableContainerStyled,
  TableStyled,
  TableHeadStyled,
  TableRowStyled,
  TableCellStyled,
  TableBodyStyled
} from './ManagementUnitTable.styles'

export const ManagementUnitTable = () => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(15)
  const [unidadesGestoras, setUnidadesGestoras] = useState<any[]>([])
  const [managementUnitList, setManagementUnitList] = useState<any[]>([])
  const { setLoading } = useLoading()
  const { showModal } = useModal()
  const [selectedManagementUnit, setSelectedManagementUnit] = useState(0)

  const handleChangePage = (newPage: number) => setPage(newPage)

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const getManagementUnitCsn = () => {
    setLoading('loading')
    managementUnitService()
      .getAll()
      .then(({ data }) => {
        setUnidadesGestoras(data)
        setLoading('idle')
      })
      .catch((error) => {
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possível carregar a lista de Unidades Gestoras',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
        setLoading('error')
      })
  }

  const getManagementUnit = () => {
    setLoading('loading')
    ParamCockpitServices()
      .getUnidadesGestoras()
      .then(({ data }) => {
        setManagementUnitList(data)
        setLoading('idle')
      })
      .catch((error) => {
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possível carregar as Unidades Gestoras cadastradas',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
        setLoading('error')
      })
  }

  const handleChangeManagementUnit = (e) => {
    setSelectedManagementUnit(e.target.value)
  }

  const addManagementUnit = () => {
    if (
      managementUnitList?.find(
        (el) => el.idUnidadeGestora === selectedManagementUnit
      )
    ) {
      showModal({
        hideButtonCancel: true,
        message:
          'Esta Unidade Gestora já foi incluída anteriormente, verifique!',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
    } else {
      setLoading('loading')
      ParamCockpitServices()
        .createUnidadeGestora({ unidadeGestoraId: selectedManagementUnit })
        .then(() => {
          getManagementUnit()
          setLoading('idle')
          showModal({
            hideButtonCancel: true,
            message: 'Unidade Gestora incluída com sucesso!',
            txtButtonConfirm: 'OK',
            type: 'success'
          })
          setSelectedManagementUnit(0)
        })
        .catch((error) => {
          showModal({
            hideButtonCancel: true,
            message: 'Erro ao incluir a Unidade Gestora, tente novamente!',
            txtButtonConfirm: 'OK',
            type: 'error'
          })
          setLoading('idle')
        })
    }
  }

  const handleDelete = (idCockpitUnidadeGestora: number) => {
    setLoading('loading')
    ParamCockpitServices()
      .deleteUnidadeGestora(idCockpitUnidadeGestora)
      .then((response) => {
        if (response.status === 200) {
          showModal({
            hideButtonCancel: true,
            message: 'Excluído com sucesso!',
            txtButtonConfirm: 'OK',
            type: 'success'
          })
          getManagementUnit()
        }
      })
      .catch((error) => {
        showModal({
          hideButtonCancel: true,
          message:
            'Ocorreu um erro ao excluir a Unidade Gestora, tente novamente!',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
        setLoading('error')
      })
      .finally(() => {
        setLoading('idle')
      })
  }

  useEffect(() => {
    getManagementUnitCsn()
    getManagementUnit()
  }, [])

  return (
    <>
      <Grid
        gridCols="3.5fr 0.4fr 4fr"
        gridGap={`0 ${SPACING.spacing20}`}
        margin={`${SPACING.spacing20} 0 ${SPACING.spacing30} 0`}
      >
        <Select
          value={selectedManagementUnit}
          onChange={handleChangeManagementUnit}
          formValue="idUnidadeGestora"
          label="Unidade Gestora (Centro Custo)"
          valueMenu="idUnidadeGestora"
          labelMenu="nmUnidade"
          listOptions={unidadesGestoras}
          required
        />
        <Button text="+" onClick={addManagementUnit} />
      </Grid>

      <Span
        text="Para cálculo de risco na visão Cockpit, desconsiderar dados abaixo:"
        fontSize={TYPOGRAPHY.bodySize16}
      />
      <Grid
        gridCols="5fr 3fr"
        gridGap={`0 ${SPACING.spacing10}`}
        margin={`${SPACING.spacing20} 0 ${SPACING.spacing30} 0`}
      >
        <TableContainerStyled>
          <TableStyled stickyHeader>
            <TableHeadStyled>
              <TableRowStyled>
                <TableCellStyled minwidth="18em">
                  Unidade Gestora (Centro Custo)
                </TableCellStyled>
                <TableCellStyled>Ação</TableCellStyled>
              </TableRowStyled>
            </TableHeadStyled>
            <TableBodyStyled>
              {(rowsPerPage > 0
                ? managementUnitList?.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage
                )
                : managementUnitList
              )?.map((row) => (
                <TableRowStyled key={row.idCockpitUnidadeGestora}>
                  <TableCellStyled>{row.nmUnidade}</TableCellStyled>
                  <TableCellStyled>
                    <DeleteIcon
                      onClick={() =>
                        showModal({
                          handleConfirm: () =>
                            handleDelete(row.idCockpitUnidadeGestora),
                          message: 'Confirma exclusão (S/N)?',
                          txtButtonCancel: 'NÃO',
                          txtButtonConfirm: 'SIM',
                          type: 'alert'
                        })
                      }
                      cursor="pointer"
                    />
                  </TableCellStyled>
                </TableRowStyled>
              ))}
            </TableBodyStyled>
          </TableStyled>

          <TableFooter
            page={page}
            rows={managementUnitList}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            typeFooter="offline"
          />
        </TableContainerStyled>
      </Grid>
    </>
  )
}
